import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Lock, ShieldCheck } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import AuroraMark from "@/components/AuroraMark";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [require2FA, setRequire2FA] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onExpired = () => toast.warning("Sesi berakhir karena tidak ada aktivitas");
    window.addEventListener("aurora:session-expired", onExpired);
    return () => window.removeEventListener("aurora:session-expired", onExpired);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await login(email, password, otp || undefined, turnstileToken || undefined);
      if (result && result.require_2fa) {
        setRequire2FA(true);
        toast.info("Masukkan 6-digit kode dari Google Authenticator");
      } else {
        toast.success("Login berhasil");
        navigate("/akses-internal-aurora/dashboard");
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#FDFBF7] grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative flex items-center gap-3">
          <AuroraMark size="h-16 w-16" />
          <div>
            <div className="font-display text-2xl">Aurora <span className="text-[#F39237]">Hijab</span></div>
            <div className="overline text-[#F39237]">Internal Access</div>
          </div>
        </div>

        <div className="relative">
          <div className="font-display italic text-[16vw] leading-none text-[#F39237]/10 select-none">Aurora</div>
        </div>

        <div className="relative">
          <div className="inline-flex items-center gap-2 overline text-[#F39237] mb-3">
            <ShieldCheck size={14} /> Secured
          </div>
          <p className="font-display italic text-2xl max-w-md leading-snug">
            "Rate limiting, 2FA, dan session timeout aktif melindungi konsol ini."
          </p>
          <div className="overline text-stone-400 mt-4">Aurora Security Layer</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 text-[#F39237] mb-3">
            <Lock size={18} />
            <span className="overline">Internal Access</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight text-[#0A0A0A] mb-3">
            {require2FA ? "Verifikasi 2FA." : "Selamat datang kembali."}
          </h1>
          <p className="text-stone-600 mb-10">
            {require2FA
              ? "Masukkan kode 6-digit dari aplikasi Google Authenticator Anda."
              : "Masuk untuk mengelola konten Aurora."}
          </p>

          <form onSubmit={onSubmit} className="space-y-6">
            {!require2FA && (
              <>
                <div>
                  <label className="block overline text-stone-500 mb-2">Email</label>
                  <input
                    type="email"
                    data-testid="login-email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 px-4 bg-transparent border border-stone-300 focus:border-[#F39237] focus:ring-2 focus:ring-[#F39237]/20 outline-none"
                    placeholder="admin@aurorahijab.co"
                  />
                </div>
                <div>
                  <label className="block overline text-stone-500 mb-2">Password</label>
                  <input
                    type="password"
                    data-testid="login-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-12 px-4 bg-transparent border border-stone-300 focus:border-[#F39237] focus:ring-2 focus:ring-[#F39237]/20 outline-none"
                    placeholder="••••••••"
                  />
                </div>
                {TURNSTILE_SITE_KEY && (
                  <div data-testid="login-turnstile">
                    <Turnstile
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={setTurnstileToken}
                      onExpire={() => setTurnstileToken("")}
                      options={{ theme: "light", size: "flexible" }}
                    />
                  </div>
                )}
              </>
            )}

            {require2FA && (
              <div>
                <label className="block overline text-stone-500 mb-2">
                  Kode Google Authenticator
                </label>
                <input
                  type="text"
                  data-testid="login-otp"
                  required
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="w-full h-14 px-4 bg-transparent border border-stone-300 focus:border-[#F39237] focus:ring-2 focus:ring-[#F39237]/20 outline-none font-mono text-2xl tracking-widest text-center"
                  placeholder="123456"
                />
                <button
                  type="button"
                  onClick={() => {
                    setRequire2FA(false);
                    setOtp("");
                  }}
                  className="mt-3 text-xs text-stone-500 underline"
                  data-testid="login-back"
                >
                  ← Kembali
                </button>
              </div>
            )}

            {error && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] transition-colors disabled:opacity-60"
            >
              {loading ? "Memproses…" : require2FA ? "Verifikasi" : "Masuk"}
              <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
